/**
 * Date rules for the request-a-call form: a weekday between tomorrow and 30 days out.
 *
 * A native date input can't gray out weekends, so weekend picks are validated
 * instead. "Today" is the America/New_York calendar day, because the server
 * (`server/app/models/api/call_requests.py`) validates against ET; a browser in
 * another zone must use the same anchor, or a boundary date it accepts can 422
 * server-side. The weekday check needs no anchor, since it works on the picked
 * calendar date.
 *
 * The helpers are pure and take an explicit `now`, so tests can pin the rules
 * and compute the next valid weekday instead of hard-coding dates that go stale.
 */

/** Local-time `YYYY-MM-DD` for `<input type="date">` values and attributes. */
export function toDateInputValue(date: Date): string {
  const y = date.getFullYear().toString().padStart(4, "0");
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Parse a date-input `YYYY-MM-DD` as local midnight.
 *
 * `new Date("YYYY-MM-DD")` parses as UTC midnight, which shifts the calendar
 * day, and so the weekday, for any viewer west of UTC. Returns null for
 * malformed values and calendar rollovers (e.g. `2026-02-31`).
 */
export function parseDateInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return toDateInputValue(parsed) === value ? parsed : null;
}

/** `en-CA` formats as `YYYY-MM-DD`; the timeZone pins the ET calendar day (DST-aware). */
const ET_DATE_FORMAT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Today's calendar date in ET, as a midnight `Date` container. */
function etToday(now: Date): Date {
  const parsed = parseDateInputValue(ET_DATE_FORMAT.format(now));
  // Unreachable, since `en-CA` always emits `YYYY-MM-DD`; it keeps the types honest.
  if (!parsed) throw new Error("unexpected Intl en-CA date format");
  return parsed;
}

/** `day` (a midnight calendar-day container) + `days`. */
function addDays(day: Date, days: number): Date {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

/** Earliest selectable date (ET tomorrow) as a date-input string for the `min` attribute. */
export function requestCallMinDate(now: Date = new Date()): string {
  return toDateInputValue(addDays(etToday(now), 1));
}

/** Latest selectable date (ET today plus 30 days) as a date-input string for the `max` attribute. */
export function requestCallMaxDate(now: Date = new Date()): string {
  return toDateInputValue(addDays(etToday(now), 30));
}

/** Why a picked date is invalid: absent/unparseable, outside ET tomorrow..+30 days, or Sat/Sun. */
export type RequestedDateIssue = "missing" | "range" | "weekend";

/** Validate a date-input value against the request-a-call rules; null = valid. */
export function validateRequestedDate(
  value: string,
  now: Date = new Date(),
): RequestedDateIssue | null {
  const picked = parseDateInputValue(value);
  if (!picked) return "missing";
  const today = etToday(now);
  if (picked < addDays(today, 1) || picked > addDays(today, 30)) return "range";
  const day = picked.getDay();
  return day === 0 || day === 6 ? "weekend" : null;
}
